import { useState, useContext, useEffect } from 'react';
import {
  Box,
  Container,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faDownload } from '@fortawesome/free-solid-svg-icons';
import { getCompanies, addCompany, editCompany, deleteCompany } from '../apis/axios';
import AuthContext from '../AuthContext';
import CommonMantineTable from '../components/companies/CompanyTable';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

// ✅ Form Modal Component
const CompanyFormModal = ({ open, onClose, onSubmit, initialValues, isEditing }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState(initialValues);

  useEffect(() => {
    setFormData(initialValues);
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!formData.name) return alert('Company name is required');
    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      {/* ===== Header ===== */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {isEditing ? 'Edit Company' : 'Add Company'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ===== Form Fields ===== */}
      <DialogContent dividers sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              placeholder="e.g. Tech Cryptors Pvt Ltd"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Manager Email"
              name="manager_email"
              type="email"
              value={formData.manager_email}
              onChange={handleChange}
              fullWidth
              placeholder="e.g. manager@gmail.com"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* ===== Actions ===== */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 3,
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            borderRadius: 3,
            textTransform: 'none',
          }}
        >
          {isEditing ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ✅ Main Component
function Companies() {
  const theme = useTheme();
  const auth = useContext(AuthContext);

  // States
  const [companies, setCompanies] = useState([]);
  const [update, setUpdate] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ id: null, name: '', manager_email: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch Companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await getCompanies();
        setCompanies(res.data);
      } catch (err) {
        console.error('Error fetching companies:', err);
      }
    };
    fetchCompanies();
  }, [update]);

  const refreshTable = () => setUpdate((prev) => !prev);

  // Export CSV
  const exportToCSV = (data, filename) => {
    const headers = ['Company', 'Devices', 'Manager Email'];
    const rows = data.map((c) => [c.name, c.device_count || '-', c.manager_email]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    link.click();
  };

  // Add/Edit Form Submit
  const handleSubmit = async (data) => {
    try {
      if (isEditing) {
        await editCompany({ ...data, id: formData.id });
        setSnackbar({ open: true, message: 'Company updated successfully!', severity: 'success' });
      } else {
        await addCompany(data);
        setSnackbar({ open: true, message: 'Company added successfully!', severity: 'success' });
      }
      refreshTable();
      setOpenModal(false);
    } catch (error) {
      console.error('Error saving company:', error);
      setSnackbar({ open: true, message: 'Failed to save company.', severity: 'error' });
    }
  };

  // Delete Company
  const handleDelete = async (id) => {
    try {
      await deleteCompany(id);
      refreshTable();
      setSnackbar({ open: true, message: 'Company deleted successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error deleting company:', error);
      setSnackbar({ open: true, message: 'Error deleting company.', severity: 'error' });
    }
  };

  // Edit Company
  const handleEdit = (company) => {
    setFormData(company);
    setIsEditing(true);
    setOpenModal(true);
  };

  // Table Columns
  const columns = [
    { accessorKey: 'name', header: 'Company Name' },
    { accessorKey: 'device_count', header: 'Devices' },
    { accessorKey: 'manager_email', header: 'Manager Email' }
  ];

  return (
    <Container
      fullWidth
      sx={{
        paddingTop: 2,
        paddingBottom: 2,
        minHeight: '100vh',
      }}
    >
      {/* ===== Header ===== */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        sx={{ width: '100%', mx: 'auto' ,   flexDirection: { xs: 'column', md: 'row' }, // 👈 Mobile = column, Desktop = row
    gap: { xs: 2, md: 0 }}}
        
      >
        <Box>
          <h1 style={{ marginBottom: '0.3rem' }}>Companies</h1>
          <p
            style={{
              fontFamily: 'Nunito Sans, sans-serif',
              fontSize: '1rem',
              color: theme.palette.text.secondary,
            }}
          >
            Manage all registered companies, their devices, and assigned managers.
          </p>
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            sx={{
              borderRadius: '20px',
              backgroundColor: theme.palette.secondary.main,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              px: 2.5,
              '&:hover': { backgroundColor: theme.palette.secondary.dark },
            }}
            onClick={() => exportToCSV(companies, 'companies.csv')}
            startIcon={<FontAwesomeIcon icon={faDownload} />}
          >
            Export CSV
          </Button>

          <Button
            variant="contained"
            sx={{
              borderRadius: '20px',
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              px: 2.5,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            }}
            onClick={() => {
              setIsEditing(false);
              setFormData({ id: null, name: '', manager_email: '' });
              setOpenModal(true);
            }}
            startIcon={<FontAwesomeIcon icon={faPlus} />}
          >
            Add Company
          </Button>
        </Box>
      </Box>

      {/* ===== Table ===== */}
      <Box sx={{ width: '100%', mx: 'auto', mt: 3 }}>
        <CommonMantineTable
          columns={columns}
          data={companies}
          onEdit={handleEdit}
          onDelete={handleDelete}
          tableStyles={{
            headerBg: theme.palette.mode === 'light' ? '#EDEBFF' : '#3B3A4D',
            tableBg: theme.palette.background.paper,
            actionEditColor: theme.palette.primary.main,
            actionDeleteColor: theme.palette.error.main,
          }}
        />
      </Box>

      {/* ===== Add/Edit Modal ===== */}
      <CompanyFormModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={handleSubmit}
        initialValues={formData}
        isEditing={isEditing}
      />

      {/* ===== Snackbar ===== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Companies;
